import { translate, currentLocale } from '../i18n';
const API_BASE = '/api/v1/platform';
const TOKEN_KEY = 'rsvs.platform.access';
const REFRESH_KEY = 'rsvs.platform.refresh';
const ADMIN_KEY = 'rsvs.platform.admin';

export function getPlatformToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setPlatformSession(data: {
  accessToken?: string;
  access_token?: string;
  refreshToken?: string;
  refresh_token?: string;
  admin?: { id: string; email: string; name: string };
}) {
  const access = data.accessToken ?? data.access_token;
  const refresh = data.refreshToken ?? data.refresh_token;
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  if (data.admin) localStorage.setItem(ADMIN_KEY, JSON.stringify(data.admin));
}

export function clearPlatformSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(ADMIN_KEY);
}

export function isPlatformAuthenticated(): boolean {
  return !!getPlatformToken();
}

export function getPlatformAdmin(): { id: string; email: string; name: string } | null {
  const raw = localStorage.getItem(ADMIN_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export class FetchError extends Error {
  statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
}

let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const rt = localStorage.getItem(REFRESH_KEY);
    if (!rt) return false;
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      setPlatformSession(data);
      return Boolean(data.access_token ?? data.accessToken);
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function platformFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const makeHeaders = (): Record<string, string> => {
    const h: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };
    const token = getPlatformToken();
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const url = path.startsWith('/') ? `${API_BASE}${path}` : `${API_BASE}/${path}`;
  let res = await fetch(url, { ...options, headers: makeHeaders() });

  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (!refreshed) {
      clearPlatformSession();
      window.location.href = '/platform/login';
      throw new FetchError(translate(currentLocale(), 'd-labels.err.unauthorized'), 401);
    }
    res = await fetch(url, { ...options, headers: makeHeaders() });
  }

  if (!res.ok) {
    let message = translate(currentLocale(), 'd-labels.err.http', { status: res.status });
    try { const body = await res.json(); message = body.message ?? body.title ?? message; } catch { /* ignore */ }
    throw new FetchError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}
